import axios from "axios";
import API_BASE_URL from "./baseUrl";

const SESSION_TOKEN_KEYS = ["token", "ownerToken", "auth_token", "jwt"];
const renewalRequests = new Map();

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem("token");
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  return config;
});

function readBearer(config) {
  const raw = config?.headers?.Authorization || config?.headers?.authorization;
  const match = typeof raw === "string" ? raw.match(/^Bearer\s+(.+)$/i) : null;
  return match?.[1]?.trim() || null;
}

function persistRenewedToken(previousToken, nextToken) {
  try {
    SESSION_TOKEN_KEYS.forEach((key) => {
      if (localStorage.getItem(key) === previousToken) {
        localStorage.setItem(key, nextToken);
      }
    });
  } catch {}
}

function renewSession(previousToken) {
  const existing = renewalRequests.get(previousToken);
  if (existing) return existing;

  const pending = axios
    .post(
      `${API_BASE_URL}/api/auth/session/renew`,
      {},
      { headers: { Authorization: `Bearer ${previousToken}` } }
    )
    .then(({ data }) => {
      const nextToken = typeof data?.token === "string" ? data.token.trim() : "";
      if (!nextToken) return null;
      persistRenewedToken(previousToken, nextToken);
      return nextToken;
    })
    .catch(() => null)
    .finally(() => renewalRequests.delete(previousToken));

  renewalRequests.set(previousToken, pending);
  return pending;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    const token = readBearer(config);
    if (
      error?.response?.status === 401 &&
      token &&
      config &&
      !config.__wrSessionRetried &&
      !String(config.url || "").includes("/api/auth/session/renew")
    ) {
      config.__wrSessionRetried = true;
      const renewedToken = await renewSession(token);
      if (renewedToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${renewedToken}`;
        return api.request(config);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
